import { Check, ExternalLink, Link2, RefreshCw } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Badge, Card, PageHeader, SectionHeading } from "../components/ui";
import { apiRequest } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useWorkspace } from "../lib/workspace";
import type { FacebookCandidate, FacebookStatus, FacebookSyncResult } from "../../shared/facebook";
import type { CapabilityStatus, Channel, Provider } from "../../shared/types";
import { defaultCapabilities } from "../../shared/domain";

const providerCopy: Record<Provider, { description: string; icon: React.ReactNode }> = {
  facebook: { description: "Messenger, Page posts and comments through Meta's official APIs.", icon: <span className="provider-letter">f</span> },
  youtube: { description: "Comment replies and user-supplied video uploads through YouTube Data API.", icon: <span className="provider-letter">▶</span> },
  tiktok: { description: "Publishing remains blocked until AutoSocial qualifies for TikTok audit.", icon: <span className="provider-letter">T</span> },
};

type BusyAction = "oauth" | "pages" | "connect" | "sync-latest" | "sync-more" | null;

export function ConnectionsPage() {
  const { data, reload } = useWorkspace();
  const { status, accessToken } = useAuth();
  const [facebookStatus, setFacebookStatus] = useState<FacebookStatus | null>(null);
  const [pages, setPages] = useState<FacebookCandidate[]>([]);
  const [selectedPageId, setSelectedPageId] = useState("");
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const selectingPage = new URLSearchParams(window.location.search).get("facebook") === "select";
  const [busy, setBusy] = useState<BusyAction>(selectingPage ? "pages" : null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [syncCursors, setSyncCursors] = useState<Record<string, string | null>>({});
  const activeProducts = useMemo(() => data?.context.products.filter((product) => product.active) ?? [], [data?.context.products]);

  useEffect(() => {
    if (status !== "authenticated" || !accessToken) return;
    let active = true;
    void apiRequest<FacebookStatus>("/api/facebook/status", accessToken)
      .then((result) => { if (active) setFacebookStatus(result); })
      .catch((reason: unknown) => { if (active) setError(errorText(reason)); });
    return () => { active = false; };
  }, [accessToken, status]);

  useEffect(() => {
    if (!selectingPage || status !== "authenticated" || !accessToken) return;
    let active = true;
    void apiRequest<{ pages: FacebookCandidate[]; hasMore: boolean }>("/api/facebook/pages", accessToken)
      .then((result) => {
        if (!active) return;
        setPages(result.pages);
        setSelectedPageId((current) => current || result.pages[0]?.id || "");
        if (result.hasMore) setNotice("More Pages are available. This setup view currently shows the first verified batch.");
      })
      .catch((reason: unknown) => { if (active) setError(errorText(reason)); })
      .finally(() => { if (active) setBusy(null); });
    return () => { active = false; };
  }, [accessToken, selectingPage, status]);

  if (!data) return null;

  async function startFacebookOAuth(includePublicActions = false) {
    if (status !== "authenticated" || !accessToken || !facebookStatus?.configured) return;
    setBusy("oauth");
    setError(null);
    try {
      const result = await apiRequest<{ url: string }>("/api/facebook/oauth/start", accessToken, { method: "POST", body: JSON.stringify({ includePublicActions }) });
      const destination = new URL(result.url);
      if (destination.protocol !== "https:") throw new Error("The Facebook authorization URL was invalid.");
      window.location.assign(destination.href);
    } catch (reason) {
      setError(errorText(reason));
      setBusy(null);
    }
  }

  async function connectSelectedPage() {
    if (!accessToken || !selectedPageId) return;
    setBusy("connect");
    setError(null);
    try {
      await apiRequest<{ channelId: string }>("/api/facebook/connect", accessToken, {
        method: "POST",
        body: JSON.stringify({ pageId: selectedPageId, productIds: selectedProductIds }),
      });
      setNotice("Facebook Page connected. Sync messages to load its current inbox; live send still requires review and backend permission checks.");
      window.history.replaceState({}, "", window.location.pathname);
      await reload();
    } catch (reason) {
      setError(errorText(reason));
    } finally {
      setBusy(null);
    }
  }

  async function syncMessages(channelId: string, mode: "latest" | "more") {
    if (!accessToken) return;
    const cursor = mode === "more" ? syncCursors[channelId] : null;
    if (mode === "more" && !cursor) return;
    setBusy(mode === "latest" ? "sync-latest" : "sync-more");
    setError(null);
    try {
      const result = await apiRequest<FacebookSyncResult>("/api/facebook/sync", accessToken, {
        method: "POST",
        body: JSON.stringify({ channelId, ...(cursor ? { cursor } : {}) }),
      });
      setSyncCursors((current) => ({ ...current, [channelId]: result.cursor }));
      const verb = mode === "latest" ? "Refreshed" : "Loaded";
      const suffix = result.hasMore ? " Use Load more conversations to continue." : mode === "latest" ? " No additional page is currently available." : " Reached the end of the available conversations.";
      setNotice(`${verb} ${result.count} conversation${result.count === 1 ? "" : "s"}.${suffix}`);
      await reload();
    } catch (reason) {
      setError(errorText(reason));
    } finally {
      setBusy(null);
    }
  }

  const displayChannels = data.channels.length > 0
    ? data.channels
    : (["facebook", "youtube", "tiktok"] as const).map((provider): Channel => ({
        id: `setup-${provider}`,
        name: `${provider[0]?.toUpperCase()}${provider.slice(1)} · setup required`,
        provider,
        handle: "No account connected",
        status: provider === "tiktok" ? "blocked" : "disconnected",
        autoReplyEnabled: false,
        productIds: [],
        capabilities: defaultCapabilities(provider),
      }));

  return (
    <>
      <PageHeader eyebrow="Official APIs only" title="Connections" description="Connect each account separately, then confirm exactly which features its current permissions allow." />
      {error ? <p className="form-error" role="alert">{error}</p> : null}
      {notice ? <p className="panel-notice" role="status">{notice}</p> : null}

      <Card className="composer-card facebook-setup-card">
        <SectionHeading
          title="Facebook setup"
          description={facebookStatus?.configured
            ? `Server configuration is present for Meta app ${facebookStatus.appId}. Page connection and live permission checks remain separate.`
            : "Pilot public pages are ready for Meta app 1450461986926279. Facebook server configuration and Page connection are not yet confirmed."}
          action={<a className="button button--secondary button--small" href="https://developers.facebook.com/apps/1450461986926279/" target="_blank" rel="noreferrer">Meta app dashboard <ExternalLink size={15} aria-hidden="true" /></a>}
        />
        <p className="panel-notice">The app is unpublished. Publishing it would not by itself grant permissions, Page tasks, OAuth access, or webhook readiness.</p>
        <ol className="setup-list" aria-label="Facebook setup progress">
          <li className="setup-step--done"><span><Check size={14} aria-hidden="true" /></span><strong>Pilot Privacy Policy ready</strong><a href="/privacy">Open policy</a></li>
          <li className="setup-step--done"><span><Check size={14} aria-hidden="true" /></span><strong>Pilot Terms of Service ready</strong><a href="/terms">Open terms</a></li>
          <li className="setup-step--done"><span><Check size={14} aria-hidden="true" /></span><strong>Manual data-deletion instructions ready</strong><a href="/data-deletion">Open instructions</a></li>
          <li><span aria-hidden="true" /><strong>OAuth connection and callback</strong><div className="facebook-setup-status"><Badge tone="amber">{facebookStatus?.configured ? "Configured · unverified" : "Pending"}</Badge></div></li>
          <li><span aria-hidden="true" /><strong>Permissions and Page tasks verification</strong><div className="facebook-setup-status"><Badge tone="amber">Pending</Badge></div></li>
          <li><span aria-hidden="true" /><strong>Webhook subscription and ingestion</strong><div className="facebook-setup-status"><Badge tone="amber">Pending</Badge></div></li>
          <li><span aria-hidden="true" /><strong>Human-reviewed test send</strong><div className="facebook-setup-status"><Badge tone="amber">Pending</Badge></div></li>
        </ol>
      </Card>

      {selectingPage && status === "authenticated" ? (
        <Card className="composer-card">
          <SectionHeading title="Choose a Facebook Page" description="Only Page summaries returned by the authenticated backend are shown. Choose the products this Page may use as business facts." />
          {busy === "pages" ? <p className="panel-notice" role="status">Loading available Pages…</p> : null}
          {pages.length > 0 ? (
            <form onSubmit={(event) => { event.preventDefault(); void connectSelectedPage(); }}>
              <fieldset className="field">
                <legend>Facebook Page</legend>
                {pages.map((page) => <label key={page.id}><input type="radio" name="facebook-page" value={page.id} checked={selectedPageId === page.id} onChange={() => setSelectedPageId(page.id)} /> {page.name}</label>)}
              </fieldset>
              <fieldset className="field">
                <legend>Active products</legend>
                {activeProducts.map((product) => (
                  <label key={product.id}><input type="checkbox" checked={selectedProductIds.includes(product.id)} onChange={(event) => setSelectedProductIds((current) => event.target.checked ? [...current, product.id] : current.filter((id) => id !== product.id))} /> {product.name}</label>
                ))}
              </fieldset>
              <button className="button button--primary" type="submit" disabled={!selectedPageId || busy !== null}>{busy === "connect" ? "Connecting…" : "Connect selected Page"}</button>
            </form>
          ) : busy !== "pages" ? <p className="panel-notice">No manageable Facebook Pages were returned for this account.</p> : null}
        </Card>
      ) : null}

      <Card>
        <SectionHeading title="Facebook posts and comments" description="Extra Page permissions are required for public posts and comment replies. Add the four permissions in the Meta Pages use case first, then authorize them here." />
        <p className="field-help">Required: pages_read_engagement, pages_read_user_content, pages_manage_posts and pages_manage_engagement. Publishing and comment sends stay blocked until permissions and Page tasks pass server checks.</p>
        <button className="button button--secondary" type="button" disabled={status !== "authenticated" || !facebookStatus?.configured || busy !== null} onClick={() => void startFacebookOAuth(true)}>Authorize posts and comments</button>
      </Card>
      <div className="connection-grid">
        {displayChannels.map((channel) => (
          <ConnectionCard
            key={channel.id}
            channel={channel}
            preview={status === "preview"}
            facebookConfigured={facebookStatus?.configured === true}
            busy={busy}
            onConnect={() => void startFacebookOAuth()}
            syncCursor={syncCursors[channel.id] ?? null}
            onSync={(mode) => void syncMessages(channel.id, mode)}
          />
        ))}
      </div>

      <Card>
        <SectionHeading title="Connection rules" description="No provider capability is treated as active until its live authorization and target access are verified." />
        <div className="rule-grid">
          <div><Check size={18} /><span><strong>Per-channel context</strong>Choose only the products relevant to each Page or channel.</span></div>
          <div><Check size={18} /><span><strong>Safety helpers tested</strong>Encryption binding, approval digest and send preflight helpers have local tests; backend status determines whether the provider flow is available.</span></div>
          <div><Check size={18} /><span><strong>Server-held access</strong>The browser receives Page summaries, never provider access tokens.</span></div>
        </div>
      </Card>
    </>
  );
}

function ConnectionCard({ channel, preview, facebookConfigured, busy, syncCursor, onConnect, onSync }: {
  channel: Channel;
  preview: boolean;
  facebookConfigured: boolean;
  busy: BusyAction;
  syncCursor: string | null;
  onConnect: () => void;
  onSync: (mode: "latest" | "more") => void;
}) {
  const available = channel.capabilities.filter((item) => item.status === "available").length;
  const pending = channel.capabilities.filter((item) => item.status === "pending_approval").length;
  const blocked = channel.capabilities.filter((item) => ["blocked", "unavailable"].includes(item.status)).length;
  const isFacebook = channel.provider === "facebook";
  const connected = isFacebook && channel.status === "connected";
  const disabled = preview || !isFacebook || (!connected && !facebookConfigured) || busy !== null;
  const actionLabel = preview
    ? "Unavailable in safe preview"
    : channel.provider === "tiktok"
      ? "Eligibility required"
      : channel.provider === "youtube"
        ? "YouTube connection unavailable"
        : connected
          ? busy === "sync-latest" ? "Refreshing…" : "Refresh latest conversations"
          : busy === "oauth" ? "Opening Facebook…" : facebookConfigured ? "Connect Facebook" : "Facebook setup incomplete";

  return (
    <Card className="connection-card">
      <div className="connection-card__header">
        <span className={`provider-icon provider-icon--${channel.provider}`}>{providerCopy[channel.provider].icon}</span>
        <div><h2>{channel.name}</h2><p>{providerCopy[channel.provider].description}</p></div>
        <Badge tone={statusTone(channel.status)}>{channel.status}</Badge>
      </div>
      <div className="capability-summary">
        <span><strong>{available}</strong> available</span>
        <span><strong>{pending}</strong> approval checks</span>
        <span><strong>{blocked}</strong> blocked</span>
      </div>
      <div className="capability-list">
        {channel.capabilities.map((capability) => (
          <div key={capability.name}>
            <span className="capability-name">{capability.name.replaceAll("_", " ")}</span>
            <Badge tone={capabilityTone(capability.status)}>{capability.status.replaceAll("_", " ")}</Badge>
            {capability.reason ? <p>{capability.reason}</p> : null}
            {capability.nextAction ? <p className="next-action">Next: {capability.nextAction}</p> : null}
          </div>
        ))}
      </div>
      <button className="button button--secondary button--full" type="button" disabled={disabled} onClick={connected ? () => onSync("latest") : onConnect}>
        {connected ? <RefreshCw size={17} /> : <Link2 size={17} />}
        {actionLabel}
        {!disabled && !connected ? <ExternalLink size={15} /> : null}
      </button>
      {connected && syncCursor ? (
        <button className="button button--secondary button--full" type="button" disabled={preview || busy !== null} onClick={() => onSync("more")}>
          <RefreshCw size={17} />
          {busy === "sync-more" ? "Loading…" : "Load more conversations"}
        </button>
      ) : null}
    </Card>
  );
}

function errorText(reason: unknown): string {
  return reason instanceof Error ? reason.message : "AutoSocial could not complete the Facebook request.";
}

function capabilityTone(status: CapabilityStatus): "green" | "amber" | "red" | "neutral" {
  if (status === "available") return "green";
  if (status === "pending_approval") return "amber";
  if (status === "blocked") return "red";
  return "neutral";
}

function statusTone(status: Channel["status"]): "green" | "amber" | "red" | "neutral" {
  if (status === "connected") return "green";
  if (status === "expired") return "amber";
  if (status === "blocked") return "red";
  return "neutral";
}
